'use client';

/**
 * LeadAuditForm — capture-only form for free brand audit requests.
 *
 * @spec /doc/specs/frontend/design-system/design-tokens-v2.md
 * @userStory US-EP022-207
 *
 * Posts to /api/marketing/lead-audit. NO LLM execution — the audit is dispatched
 * manually by the SkuLift team. The form captures URL + email + (optional)
 * company + (optional) priority queries.
 */

import { useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { ScanLine, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/marketing/ui/card';
import { Button } from '@/components/marketing/ui/button';
import { cn } from '@/lib/utils';

type FormState = 'idle' | 'sending' | 'success' | 'error' | 'rate-limit';

export interface LeadAuditFormProps {
  className?: string;
}

const FIELD_CLASSES =
  'w-full rounded-lg bg-ink-900/60 border border-white/10 focus:border-cyan-500/60 focus:ring-2 focus:ring-cyan-500/30 focus:outline-none px-4 py-3 text-sm text-white placeholder:text-white/40 transition-colors';

export function LeadAuditForm({ className }: LeadAuditFormProps) {
  const t = useTranslations('marketing.leadAudit.form');
  const locale = useLocale();
  const [state, setState] = useState<FormState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setState('sending');
    setErrorMsg(null);

    const formData = new FormData(e.currentTarget);
    const payload = {
      url: String(formData.get('url') ?? ''),
      email: String(formData.get('email') ?? ''),
      company: String(formData.get('company') ?? ''),
      queries: String(formData.get('queries') ?? ''),
      consent: formData.get('consent') === 'on',
      locale,
      // Honeypot — bots fill this
      website: String(formData.get('website') ?? ''),
    };

    try {
      const res = await fetch('/api/marketing/lead-audit', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.status === 429) {
        setState('rate-limit');
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setErrorMsg((body as { error?: string }).error ?? null);
        setState('error');
        return;
      }
      setState('success');
      e.currentTarget.reset();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : null);
      setState('error');
    }
  }

  return (
    <Card variant="glass-strong" className={cn('w-full', className)}>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {/* Honeypot — visually hidden but in-DOM */}
        <label className="sr-only" aria-hidden="true">
          Website (do not fill)
          <input type="text" name="website" tabIndex={-1} autoComplete="off" defaultValue="" />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-white/80">{t('url')}</span>
          <input
            type="url"
            name="url"
            required
            inputMode="url"
            placeholder={t('urlPlaceholder')}
            className={cn(FIELD_CLASSES, 'mt-2')}
            autoComplete="url"
          />
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-sm font-medium text-white/80">{t('email')}</span>
            <input
              type="email"
              name="email"
              required
              placeholder={t('emailPlaceholder')}
              className={cn(FIELD_CLASSES, 'mt-2')}
              autoComplete="email"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-white/80">{t('company')}</span>
            <input
              type="text"
              name="company"
              placeholder={t('companyPlaceholder')}
              className={cn(FIELD_CLASSES, 'mt-2')}
              autoComplete="organization"
            />
          </label>
        </div>

        <label className="block">
          <span className="text-sm font-medium text-white/80">{t('queriesHint')}</span>
          <textarea
            name="queries"
            rows={4}
            placeholder={t('queriesPlaceholder')}
            className={cn(FIELD_CLASSES, 'mt-2 min-h-[100px] resize-y')}
          />
        </label>

        <label className="flex items-start gap-3 text-sm text-white/70">
          <input type="checkbox" name="consent" required className="mt-1 h-4 w-4 accent-cyan-500" />
          <span>{t('consent')}</span>
        </label>

        <div className="pt-2">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={state === 'sending'}
            iconRight={<ScanLine size={18} />}
            className="w-full sm:w-auto"
          >
            {state === 'sending' ? t('sending') : t('submit')}
          </Button>
        </div>

        {state === 'success' ? (
          <p
            role="status"
            className="flex items-start gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-300/90"
          >
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>{t('success')}</span>
          </p>
        ) : null}
        {state === 'error' ? (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300/90"
          >
            <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>{errorMsg ?? t('error')}</span>
          </p>
        ) : null}
        {state === 'rate-limit' ? (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-300/90"
          >
            <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>{t('rateLimit')}</span>
          </p>
        ) : null}
      </form>
    </Card>
  );
}
